import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PropertyCard } from "@/components/PropertyCard";
import { Button } from "@/components/ui/button";
import {
  Baby,
  Wind,
  Cat,
  HeartPulse,
  Flower2,
  Sparkles,
  ArrowRight,
  ShieldCheck,
  Droplet,
  Check,
} from "lucide-react";

/** Wellness-ready threshold — properties with freshness_score ≥ 95 are
 *  eligible to be surfaced on this page as "safe stays." */
const WELLNESS_MIN_SCORE = 95;

/** Same lightweight SEO hook used on CityPage. */
function useSEO({ title, description, keywords, image, url }) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;
    const setMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setCanonical = (href) => {
      let el = document.querySelector('link[rel="canonical"]');
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", "canonical");
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };
    setMeta("description", description);
    setMeta("keywords", keywords);
    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:image", image, true);
    setMeta("og:url", url, true);
    setMeta("og:type", "website", true);
    setMeta("twitter:card", "summary_large_image");
    setCanonical(url);
    return () => { document.title = prevTitle; };
  }, [title, description, keywords, image, url]);
}

const SEGMENTS = [
  {
    icon: Baby,
    tone: "emerald",
    title: "Families with young children",
    body: "Babies breathe faster than adults and take in more airborne particles per pound of bodyweight. Every certified stay documents PM2.5, CO₂, and cleaning turnaround so a first birthday getaway doesn't turn into a first ER visit.",
    testid: "wellness-segment-families",
  },
  {
    icon: Wind,
    tone: "sky",
    title: "Asthma & allergy sufferers",
    body: "Dust mites, mildew, and lingering fragrance sprays are the top three asthma triggers in short-term rentals. Guest Smell Certified™ properties are dehumidified into the 30-55% safe zone and cleaned with fragrance-free protocols on request.",
    testid: "wellness-segment-asthma",
  },
  {
    icon: Cat,
    tone: "amber",
    title: "Pet-allergy sufferers",
    body: "Prior pet occupancy is the single most-hidden variable in short-term rentals — dander clings to carpets, upholstery, and HVAC filters for months. Ask any Guest Smell Certified™ host for their pet-history disclosure; Premium-tier hosts publish it up front.",
    testid: "wellness-segment-pet-allergy",
  },
  {
    icon: HeartPulse,
    tone: "rose",
    title: "Immunocompromised & medical-stay guests",
    body: "Traveling for cancer treatment, transplant recovery, or an elderly parent's specialist visit? Certified turnovers are time-stamped, HEPA-filter ages are logged, and any check-in flagged as immunocompromised triggers a fresh-linen swap that our software confirms.",
    testid: "wellness-segment-immunocompromised",
  },
  {
    icon: Flower2,
    tone: "violet",
    title: "Pregnant travelers",
    body: "Formaldehyde from new furniture and residual cleaning chemicals disproportionately affect fetal development. Certified stays disclose furnishing age and use low-VOC cleaning products — because a babymoon should not require reading an SDS sheet.",
    testid: "wellness-segment-pregnant",
  },
];

const TONE_STYLES = {
  emerald: { chip: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: "bg-emerald-50 text-emerald-600" },
  sky:     { chip: "bg-sky-50 text-sky-700 border-sky-200",           icon: "bg-sky-50 text-sky-600" },
  amber:   { chip: "bg-amber-50 text-amber-700 border-amber-200",     icon: "bg-amber-50 text-amber-600" },
  rose:    { chip: "bg-rose-50 text-rose-700 border-rose-200",        icon: "bg-rose-50 text-rose-600" },
  violet:  { chip: "bg-violet-50 text-violet-700 border-violet-200",  icon: "bg-violet-50 text-violet-600" },
};

const CHECKLIST = [
  "PM2.5 & CO₂ air-quality monitoring on record",
  "Humidity held in the 30-55% asthma-safe range",
  "Time-stamped cleaning turnover between every guest",
  "HVAC filter age logged and rotated on schedule",
  "Pet-occupancy history disclosed on request (Premium: always)",
  "Fragrance-free / low-VOC cleaning protocol available",
];

export default function Wellness() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/properties", { params: { min_score: WELLNESS_MIN_SCORE } })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, []);

  const featured = useMemo(() => items.slice(0, 6), [items]);
  const avgScore = useMemo(() => {
    if (!items.length) return null;
    const scores = items.map((p) => p.freshness_score).filter((s) => typeof s === "number");
    if (!scores.length) return null;
    return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  }, [items]);

  useSEO({
    title: "Wellness-Ready Stays · Guest Smell Certified™",
    description:
      "Certified fresh short-term rentals for families with young children, asthma & pet-allergy sufferers, immunocompromised travelers, and pregnant guests. Verified air quality, humidity, and cleaning standards.",
    keywords:
      "hypoallergenic Airbnb, pet-free rental, asthma-safe stay, allergy-friendly vacation rental, immunocompromised travel, family clean rental, HEPA filtered Airbnb, low-VOC rental",
    image: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1600",
    url: "https://guestsmell.com/wellness",
  });

  return (
    <div className="scent-wave" data-testid="wellness-page">
      {/* HERO ------------------------------------------------------------ */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs tracking-[0.18em] uppercase font-semibold"
              data-testid="wellness-eyebrow"
            >
              <Sparkles className="w-3 h-3" />
              Wellness-Ready · Freshness ≥ {WELLNESS_MIN_SCORE}
            </div>
            <h1
              className="mt-6 font-serif text-5xl md:text-6xl leading-[1.05] tracking-tight text-[#0A1128]"
              data-testid="wellness-heading"
            >
              For <span className="italic text-emerald-600">everyone</span> with a concern about the air they breathe.
            </h1>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-lg">
              Cleanliness is a checklist item. Wellness is a promise. Guest Smell Certified™
              stays document what other listings hide — so families, allergy sufferers, and
              anyone healing can book without holding their breath.
            </p>

            <div className="mt-10 flex items-center gap-8 text-sm text-slate-500">
              <div>
                <span className="font-serif text-3xl text-[#0A1128]" data-testid="wellness-stat-count">
                  {loading ? "…" : items.length}
                </span>
                <div className="text-xs uppercase tracking-[0.2em] mt-1">wellness-ready stays</div>
              </div>
              {avgScore != null && (
                <>
                  <div className="w-px h-10 bg-slate-300" />
                  <div>
                    <span className="font-serif text-3xl text-[#0A1128]" data-testid="wellness-stat-avg">
                      {avgScore}
                    </span>
                    <div className="text-xs uppercase tracking-[0.2em] mt-1">avg freshness score</div>
                  </div>
                </>
              )}
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
              <Link to="/browse?min_score=95" data-testid="wellness-browse-cta">
                <Button size="lg" className="rounded-full bg-[#0A1128] hover:bg-[#1A2A3A] text-white px-8">
                  Browse wellness-ready stays <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/register" data-testid="wellness-host-cta">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full border-[#D4AF37] text-[#0A1128] hover:bg-[#F9F3E5] px-8"
                >
                  Host? Certify your property →
                </Button>
              </Link>
            </div>
          </div>

          {/* Hero image */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1631679706909-1844bbd07221?w=1200"
                alt="A calm, sunlit bedroom in a certified wellness-ready short-term rental"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128]/50 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-xl border border-slate-200 hidden md:block max-w-[260px]">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center">
                  <ShieldCheck className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-emerald-700 font-semibold">
                  What we verify
                </div>
              </div>
              <ul className="mt-3 space-y-2 text-xs text-slate-600">
                {CHECKLIST.slice(0, 3).map((c) => (
                  <li key={c} className="flex items-start gap-2">
                    <Check className="w-3 h-3 text-emerald-500 mt-0.5 shrink-0" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* AUDIENCE SEGMENTS ---------------------------------------------- */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-20" data-testid="wellness-segments">
        <div className="max-w-2xl">
          <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-semibold">
            Who this is for
          </div>
          <h2 className="mt-2 font-serif text-3xl md:text-4xl text-[#0A1128] tracking-tight">
            Nobody should have to guess about the air in a rental.
          </h2>
          <p className="mt-3 text-slate-600">
            Short-term rentals list bedrooms and Wi-Fi speeds. They rarely list dander history, humidity, or filter age.
            We fix that — for the guests who can&apos;t afford the coin flip.
          </p>
        </div>

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SEGMENTS.map((s) => {
            const tone = TONE_STYLES[s.tone];
            const Icon = s.icon;
            return (
              <div
                key={s.title}
                className="bg-white rounded-3xl border border-slate-200 p-6 hover:-translate-y-1 hover:shadow-xl transition-transform duration-200"
                data-testid={s.testid}
              >
                <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${tone.icon}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="mt-5 font-serif text-xl text-[#0A1128]">{s.title}</h3>
                <p className="mt-3 text-slate-600 text-sm leading-relaxed">{s.body}</p>
              </div>
            );
          })}

          {/* Sixth tile: catch-all invitation */}
          <div className="rounded-3xl border border-dashed border-emerald-300 bg-emerald-50/40 p-6 flex flex-col justify-between">
            <div>
              <div className="w-11 h-11 rounded-2xl bg-emerald-100 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-emerald-700" />
              </div>
              <h3 className="mt-5 font-serif text-xl text-[#0A1128]">…and anyone else with a concern.</h3>
              <p className="mt-3 text-slate-600 text-sm leading-relaxed">
                Elderly travelers, chronic-migraine sufferers, MCS, post-COVID recovery, chemical sensitivities.
                If the air you breathe matters to you, this list matters to you.
              </p>
            </div>
            <Link to="/contact" className="mt-5 text-emerald-700 text-sm font-medium inline-flex items-center gap-1 hover:underline" data-testid="wellness-contact-link">
              Talk to us about your specific need <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* THE STANDARD --------------------------------------------------- */}
      <section className="bg-[#0A1128] text-white" data-testid="wellness-standard">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] font-semibold">
                The Wellness Standard
              </div>
              <h2 className="mt-2 font-serif text-3xl md:text-4xl tracking-tight">
                Six checks a listing must publish before it earns the badge.
              </h2>
              <p className="mt-4 text-slate-300 leading-relaxed max-w-lg">
                No self-attestation. No &ldquo;professionally cleaned&rdquo; hand-wave. Just documented,
                time-stamped, guest-visible verification — the same standard hospitals apply
                to shared airspace.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <div className="flex items-center gap-2 text-sm text-slate-300">
                  <Wind className="w-4 h-4 text-[#D4AF37]" /> Air
                </div>
                <div className="w-px h-4 bg-slate-600" />
                <div className="flex items-center gap-2 text-sm text-slate-300">
                  <Droplet className="w-4 h-4 text-[#D4AF37]" /> Humidity
                </div>
                <div className="w-px h-4 bg-slate-600" />
                <div className="flex items-center gap-2 text-sm text-slate-300">
                  <ShieldCheck className="w-4 h-4 text-[#D4AF37]" /> Turnover
                </div>
              </div>
            </div>

            <ul className="space-y-3" data-testid="wellness-checklist">
              {CHECKLIST.map((c) => (
                <li
                  key={c}
                  className="flex items-start gap-3 bg-white/5 border border-white/10 rounded-2xl px-5 py-4"
                >
                  <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-emerald-300" />
                  </div>
                  <span className="text-sm text-slate-200 leading-relaxed">{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* WELLNESS-READY GRID -------------------------------------------- */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20" data-testid="wellness-featured">
        <div className="flex items-end justify-between mb-6 gap-6 flex-wrap">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-semibold">
              Freshness ≥ {WELLNESS_MIN_SCORE}
            </div>
            <h2 className="mt-2 font-serif text-3xl md:text-4xl text-[#0A1128] tracking-tight">
              Wellness-ready stays available now
            </h2>
          </div>
          <Link to="/browse?min_score=95" className="text-emerald-700 text-sm font-medium inline-flex items-center gap-1 hover:underline">
            See all {items.length} wellness-ready stays <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {loading ? (
          <div className="mt-8 text-slate-500">Loading wellness-ready stays…</div>
        ) : featured.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
            No stays currently score ≥ {WELLNESS_MIN_SCORE}. Try <Link to="/browse" className="text-emerald-700 underline">browsing all certified stays</Link>.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p) => <PropertyCard key={p.id} p={p} />)}
          </div>
        )}
      </section>

      {/* CTA STRIP ------------------------------------------------------ */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-[#F9F3E5] border border-emerald-100 p-10 md:p-14 flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-semibold">
              Own a rental?
            </div>
            <h3 className="mt-2 font-serif text-2xl md:text-3xl text-[#0A1128] tracking-tight max-w-xl">
              Get the Wellness-Ready badge and rank higher for the guests who care most.
            </h3>
            <p className="mt-3 text-slate-600 max-w-lg">
              Certified hosts routinely see 12-18% higher nightly rates when guests can verify
              the air, humidity, and turnover history themselves.
            </p>
          </div>
          <Link to="/register" data-testid="wellness-cta-strip-host">
            <Button size="lg" className="rounded-full bg-[#0A1128] hover:bg-[#1A2A3A] text-white px-8 whitespace-nowrap">
              Certify your property <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
